import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetMoneyPages as GetMoneyPagesQuery,
    GetMoneyPages_maUrls_edges_node as MoneyPage,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../apollo-client';

import GET_MONEY_PAGES from '../queries/get-money-pages.graphql';

export const GET_MONEY_PAGES_LIMIT = 20;

export const useGetMoneyPages = (): {
    moneyPages: MoneyPage[];
    isLoading: boolean;
    hasNextPage: boolean;
    totalCount: number;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
} => {
    const variables = {
        first: GET_MONEY_PAGES_LIMIT,
    } as const;

    const {loading, data, error, refetch, fetchMore} = useQuery<
        GetMoneyPagesQuery,
        Record<any, any>
    >(GET_MONEY_PAGES, {
        fetchPolicy: 'cache-and-network',
        variables,
    });

    const pageInfo = data && data.maUrls && data.maUrls.pageInfo;
    const hasNextPage = pageInfo && pageInfo.hasNextPage;
    const hasPreviousPage = pageInfo && pageInfo.hasPreviousPage;
    const endCursor = pageInfo && pageInfo.endCursor;
    const startCursor = pageInfo && pageInfo.startCursor;
    const totalCount = data && data.maUrls ? data.maUrls.totalCount : 0;

    const fetchPage = (direction: 'next' | 'previous') => {
        const isNext = direction === 'next';
        const canFetch = isNext ? hasNextPage : hasPreviousPage;

        if (!canFetch) {
            return Promise.resolve();
        }

        const pageVariables = isNext
            ? {first: GET_MONEY_PAGES_LIMIT, after: endCursor, last: null, before: null}
            : {last: GET_MONEY_PAGES_LIMIT, before: startCursor, first: null, after: null};

        return fetchMore({
            variables: pageVariables,
            updateQuery: (prev: GetMoneyPagesQuery, {fetchMoreResult}) => {
                if (!fetchMoreResult) return prev;

                nutApolloClient.writeQuery({
                    query: GET_MONEY_PAGES,
                    variables: pageVariables,
                    data: fetchMoreResult,
                });

                return fetchMoreResult;
            },
        });
    };

    return {
        moneyPages:
            data && data.maUrls && data.maUrls.edges
                ? data.maUrls.edges.map((edge) => edge.node)
                : [],
        isLoading: loading,
        hasNextPage: hasNextPage || false,
        totalCount: totalCount,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        fetchNextPage: () => fetchPage('next'),
        fetchPreviousPage: () => fetchPage('previous'),
    };
};
